import type { components } from '@intelliauth/api-client-v2-cp/types'
import { AmbientBackground, Avatar, IntelliAuthLogo, ThemeToggle, TopHeader } from '@intelliauth/ui'
import {
  ChevronDown,
  Circle,
  Contact,
  CreditCard,
  History,
  LayoutDashboard,
  LogOut,
  Menu,
  Server,
  Settings,
  User,
  Users,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { cpClient } from '@/lib/api/cpClient'
import { tenantBaseUrl } from '@/lib/domain'
import { cn } from '@/lib/utils'

type Tenant = components['schemas']['Tenant']

const NAV_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: Server, label: 'Tenants', path: '/dashboard/tenants' },
  { icon: Contact, label: 'Identities', path: '/dashboard/identities' },
  { icon: Users, label: 'Members', path: '/dashboard/members' },
  { icon: History, label: 'Audit', path: '/dashboard/audit' },
  { icon: CreditCard, label: 'Billing', path: '/dashboard/billing' },
  { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
]

export default function DashboardLayout() {
  const location = useLocation()
  const { user, currentOrg, logout } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [tenants, setTenants] = useState<Tenant[]>([])

  const fetchTenants = useCallback(async () => {
    if (!currentOrg) return
    try {
      const env = await cpClient().get('/api/v1/organizations/{id}/tenants', {
        path: { id: currentOrg.id },
      })
      const data = (env as { data: Tenant[] }).data ?? []
      setTenants(data)
    } catch {
      // Sidebar tenants are non-critical — fail silently
    }
  }, [currentOrg])

  useEffect(() => {
    fetchTenants()
  }, [fetchTenants])

  const userInitials = user?.email ? user.email.substring(0, 2).toUpperCase() : '??'

  return (
    <div className="min-h-screen bg-background">
      {/* ── Mobile sidebar overlay ─────────────────────────── */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="fixed inset-0 bg-foreground/50 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 w-[280px] z-50">
            <Sidebar
              currentPath={location.pathname}
              onClose={() => setSidebarOpen(false)}
              currentOrg={currentOrg}
              tenants={tenants}
            />
          </div>
        </div>
      )}

      {/* ── Desktop sidebar ────────────────────────────────── */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:block lg:w-[280px]">
        <Sidebar currentPath={location.pathname} currentOrg={currentOrg} tenants={tenants} />
      </div>

      {/* ── Main content area ──────────────────────────────── */}
      <div className="lg:pl-[280px]">
        <div className="sticky top-0 z-20">
          <TopHeader
            title={
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(true)}
                  aria-label="Open navigation menu"
                  className="lg:hidden p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted"
                >
                  <Menu className="h-5 w-5" />
                </button>
                <div className="flex items-center text-sm font-normal">
                  <span className="text-muted-foreground">Organization</span>
                  <ChevronDown className="h-3 w-3 mx-1.5 text-muted-foreground/70 rotate-[-90deg]" />
                  <span className="text-foreground font-medium">Dashboard</span>
                </div>
              </div>
            }
            rightSlot={<ThemeToggle variant="compact" />}
            userSlot={
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  aria-label="Open user menu"
                  className="flex items-center gap-2 p-1 rounded-pill hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Avatar size="sm" initials={userInitials} />
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </button>

                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-30"
                      onClick={() => setUserMenuOpen(false)}
                      aria-hidden
                    />
                    <div
                      role="menu"
                      className="absolute right-0 mt-2 w-56 z-40 bg-popover border border-border shadow-lg rounded-lg py-1"
                    >
                      <div className="px-3 py-2 border-b border-border">
                        <p className="text-sm font-medium text-foreground truncate">
                          {user?.email ?? 'Unknown'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {currentOrg?.name ?? 'No organization'}
                        </p>
                      </div>
                      <a
                        href="#"
                        role="menuitem"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-foreground/[0.06]"
                      >
                        <User className="h-4 w-4 text-muted-foreground" />
                        Profile
                      </a>
                      <a
                        href="#"
                        role="menuitem"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-foreground/[0.06]"
                      >
                        <Settings className="h-4 w-4 text-muted-foreground" />
                        Settings
                      </a>
                      <div className="border-t border-border mt-1 pt-1">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setUserMenuOpen(false)
                            logout()
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-b-lg"
                        >
                          <LogOut className="h-4 w-4" />
                          Sign out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            }
          />
        </div>

        <main className="relative p-4 sm:p-8 max-w-[1200px]">
          <AmbientBackground variant="subtle" />
          <Outlet />
        </main>
      </div>
    </div>
  )
}

// ── Sidebar Component ────────────────────────────────────────────

function Sidebar({
  currentPath,
  onClose,
  currentOrg,
  tenants,
}: {
  currentPath: string
  onClose?: () => void
  currentOrg?: { id: string; name: string; slug: string; plan: string } | null
  tenants?: Tenant[]
}) {
  return (
    <div className="flex flex-col h-full bg-sidebar text-sidebar-foreground backdrop-blur-xl border-r border-sidebar-border">
      {/* Logo — IntelliAuthLogo accepts `variant`. In light mode the
          sidebar bg is white so the dark-on-light logo (`dark`) is
          correct; dark mode flips to light-on-dark via the auto
          variant. */}
      <div className="flex items-center justify-between h-16 px-5 border-b border-sidebar-border">
        <Link to="/" className="flex items-center gap-2">
          <IntelliAuthLogo size="sm" variant="auto" />
        </Link>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden p-1 text-sidebar-foreground/60 hover:text-sidebar-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Organization */}
      <div className="px-4 py-3">
        <p className="text-sm font-semibold text-sidebar-foreground truncate">
          {currentOrg?.name ?? 'No organization'}
        </p>
        <p className="text-xs text-sidebar-foreground/60 truncate">{currentOrg?.slug ?? ''}</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        <div className="mb-2">
          <p className="px-3 text-[11px] font-semibold text-[--color-ia-sidebar-section-label] uppercase tracking-wider">
            Organization
          </p>
        </div>
        {NAV_ITEMS.map((item) => {
          const isActive =
            item.path === '/dashboard'
              ? currentPath === '/dashboard'
              : currentPath.startsWith(item.path)
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'flex items-center gap-3 h-11 px-3 rounded-md text-sm transition-colors',
                isActive
                  ? 'bg-[--color-ia-sidebar-active-bg] text-[--color-ia-sidebar-active-fg] border-l-[3px] border-[--color-ia-sidebar-active-border] font-medium'
                  : 'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent',
              )}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {item.label}
            </Link>
          )
        })}

        {/* Tenant quick-access list */}
        {tenants && tenants.length > 0 && (
          <>
            <div className="mt-5 mb-2">
              <p className="px-3 text-[11px] font-semibold text-[--color-ia-sidebar-section-label] uppercase tracking-wider">
                Tenants
              </p>
            </div>
            {tenants.slice(0, 5).map((t) => {
              const adminUrl =
                t.status === 'active' && currentOrg
                  ? tenantBaseUrl(t.slug, currentOrg.slug, { path: '/admin' })
                  : undefined
              return (
                <a
                  key={t.id}
                  href={adminUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'flex items-center gap-3 h-9 px-3 rounded-md text-sm transition-colors',
                    'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent',
                  )}
                >
                  <Circle
                    className={cn(
                      'h-2 w-2 flex-shrink-0 fill-current',
                      t.status === 'active'
                        ? 'text-emerald-500'
                        : t.status === 'pending'
                          ? 'text-blue-500 animate-pulse'
                          : t.status === 'suspended'
                            ? 'text-amber-500'
                            : 'text-gray-400',
                    )}
                  />
                  <span className="truncate">{t.name}</span>
                  {t.status === 'active' ? (
                    <span className="ml-auto text-[10px] text-sidebar-foreground/60 flex-shrink-0">
                      {t.region}
                    </span>
                  ) : t.status === 'pending' ? (
                    <span className="ml-auto text-[10px] text-blue-500 font-medium flex-shrink-0">
                      Provisioning...
                    </span>
                  ) : t.status === 'suspended' ? (
                    <span className="ml-auto text-[10px] text-amber-500 font-medium flex-shrink-0">
                      Suspended
                    </span>
                  ) : (
                    <span className="ml-auto text-[10px] text-sidebar-foreground/60 flex-shrink-0">
                      {t.region}
                    </span>
                  )}
                </a>
              )
            })}
            {tenants.length > 5 && (
              <Link
                to="/dashboard/tenants"
                className="flex items-center gap-3 h-9 px-3 rounded-md text-xs text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors"
              >
                +{tenants.length - 5} more
              </Link>
            )}
          </>
        )}
      </nav>
    </div>
  )
}
